import type { CallTaskEventId } from "@repo/app-types/entities/callTaskEvent";
import type { CustomerTimelineTeammate } from "@repo/app-types/entities/performanceCustomer";
import type { RecordingId } from "@repo/app-types/entities/recording";
import {
  buildTeammateDisplayNames,
  type TeammateId,
} from "@repo/app-types/entities/teammate";
import type { GradingId } from "@repo/app-types/entities/teammatePerformanceSummary";
import type { CustomerTimelineRowDetailsParticipant } from "@repo/ui/customerTimeline/customerTimelineRowDetails";
import {
  teammateLabelKind,
  type TeammateLabelProps,
} from "@repo/ui/teammate/teammateLabel";
import type { ReactNode } from "react";

type CustomerInteractionTarget =
  | { recordingId: RecordingId }
  | { textConversationId: CallTaskEventId };

/**
 * Opens an interaction's slideover, at a call or text conversation if one is
 * given.
 */
export type OpenCustomerInteraction = (
  gradingId: GradingId,
  target?: CustomerInteractionTarget,
) => void;

/** What an interaction row draws: its icon on the rail, and its details. */
export interface InteractionRowParts {
  icon: ReactNode;
  details: ReactNode;
}

export interface CustomerTimelineRowOptions {
  openInteraction: OpenCustomerInteraction;
  /** The teammate whose page this is, if any. */
  viewedTeammateId: TeammateId | undefined;
}

/** A voice agent shows its persona name, with its configuration beside it. */
export function buildCustomerTimelineTeammateLabel(
  teammate: CustomerTimelineTeammate,
): TeammateLabelProps {
  const name = buildTeammateDisplayNames(teammate).primaryDisplayName;
  const kind = teammateLabelKind(teammate.type);
  // An agent without its own persona name uses its configuration name, so
  // show the name once.
  return teammate.persona_name && teammate.persona_name !== name
    ? { name: teammate.persona_name, kind, configurationName: name }
    : { name, kind };
}

export function buildCustomerTimelineParticipant(
  teammate: CustomerTimelineTeammate,
): CustomerTimelineRowDetailsParticipant {
  return { id: teammate.id, ...buildCustomerTimelineTeammateLabel(teammate) };
}

/** Muted text after a row's title, such as a call's length. */
export function renderTrailingText(text: string): ReactNode {
  return (
    <span className="text-xs font-normal text-muted-foreground">{text}</span>
  );
}
